/**
 * TerminalSurface — the sanctioned terminal-aesthetic surface for Workbench
 * apps that render a console/log view (Script Runner, System Logs).
 *
 * WHY THIS EXISTS
 * ───────────────
 * A terminal is the one Workbench context where Fluent `tokens.*` deliberately
 * do NOT apply: a console must read as a *fixed* GitHub-dark surface in BOTH
 * light and dark Workbench themes. Before this primitive, Script Runner and
 * System Logs each hand-rolled the same GitHub-dark palette (~25 hex literals
 * between them). The palette now lives in `./terminalPalette` (the ONE
 * sanctioned home); this file is the matching chrome.
 *
 * Like `palette.ts`, both modules live in `platform/` (allowlisted by
 * `check_frontend_design.py`), so the raw hex they carry is sanctioned; app
 * code that reaches for the same hex is not.
 *
 * COMPOSITION (named exports — one per platform convention)
 * ──────────────────────────────────────────────────────────
 *   <TerminalSurface>                     dark mono surface (fills its parent)
 *     <TerminalHeader>…</TerminalHeader>  top bar (title left, controls right)
 *     <TerminalBody scrollRef={…}>
 *       <TerminalLine tone="success">✓ accepted</TerminalLine>
 *       …
 *     </TerminalBody>
 *     <TerminalStatusBar>…</TerminalStatusBar>   bottom status strip
 *   </TerminalSurface>
 *
 * For free-form rows (multi-column log lines) read tones straight off
 * `terminalPalette` / `terminalLineColor` / `terminalSeverityColor` from
 * `./terminalPalette`.
 */
import type { CSSProperties, ReactNode, Ref } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { terminalPalette, terminalLineColor, type TerminalLineTone } from './terminalPalette';

const useStyles = makeStyles({
  surface: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: terminalPalette.background,
    color: terminalPalette.foreground,
    fontFamily: tokens.fontFamilyMonospace,
    overflow: 'hidden',
  },
  header: {
    padding: '8px 12px',
    borderBottom: `1px solid ${terminalPalette.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: '11px',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '12px',
    fontSize: '12px',
    lineHeight: 1.7,
  },
  line: {
    marginBottom: '2px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  statusBar: {
    minHeight: '28px',
    padding: '0 12px',
    borderTop: `1px solid ${terminalPalette.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '11px',
    color: terminalPalette.subtle,
    flexShrink: 0,
  },
});

interface TerminalSurfaceProps {
  children: ReactNode;
  className?: string;
}

/** Root dark mono surface. Fills its parent (give it a sized container). */
export function TerminalSurface({ children, className }: TerminalSurfaceProps) {
  const styles = useStyles();
  return <div className={mergeClasses(styles.surface, className)}>{children}</div>;
}

interface TerminalHeaderProps {
  children: ReactNode;
  className?: string;
}

/** Top bar — title on the left, controls on the right. */
export function TerminalHeader({ children, className }: TerminalHeaderProps) {
  const styles = useStyles();
  return <div className={mergeClasses(styles.header, className)}>{children}</div>;
}

interface TerminalBodyProps {
  children: ReactNode;
  /** Ref to the scrollable body — drive auto-scroll-to-bottom from the app. */
  scrollRef?: Ref<HTMLDivElement>;
  className?: string;
}

/** Scrollable console body. */
export function TerminalBody({ children, scrollRef, className }: TerminalBodyProps) {
  const styles = useStyles();
  return (
    <div ref={scrollRef} className={mergeClasses(styles.body, className)}>
      {children}
    </div>
  );
}

interface TerminalLineProps {
  children: ReactNode;
  /** Semantic tone — maps to the sanctioned line palette. */
  tone?: TerminalLineTone;
  /** Explicit color override (still sourced from `terminalPalette` in callers). */
  color?: string;
  style?: CSSProperties;
}

/** A single streamed output line, colored by semantic `tone`. */
export function TerminalLine({ children, tone = 'system', color, style }: TerminalLineProps) {
  const styles = useStyles();
  return (
    <div
      className={styles.line}
      style={{ color: color ?? terminalLineColor(tone), ...style }}
    >
      {children}
    </div>
  );
}

interface TerminalStatusBarProps {
  children: ReactNode;
  className?: string;
}

/** Bottom status strip. */
export function TerminalStatusBar({ children, className }: TerminalStatusBarProps) {
  const styles = useStyles();
  return <div className={mergeClasses(styles.statusBar, className)}>{children}</div>;
}
